import type { GradebookHomeworkCell, GradebookScoreCell } from '../types';
import { formatNumber } from './gradeSheet';

export type CellTone = 'good' | 'fair' | 'poor' | 'absent' | 'muted' | 'plain';

export interface CellView {
    text: string;
    title?: string;
    tone: CellTone;
}

const WEEKDAYS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

// "2026-10-06" → { weekday: 'T3', day: '06/10' }
export function dateColumnLabel(dateKey: string) {
    const [year, month, day] = dateKey.split('-').map(Number);
    const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
    return { weekday, day: `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}` };
}

export function completionTone(percent: number | null): CellTone {
    if (percent === null) return 'plain';
    if (percent >= 80) return 'good';
    if (percent >= 50) return 'fair';
    return 'poor';
}

export function homeworkCellView(cell: GradebookHomeworkCell): CellView {
    switch (cell.kind) {
        case 'scored': {
            const percent = cell.assigned > 0 ? cell.completed / cell.assigned * 100 : null;
            return {
                text: `${formatNumber(cell.correct)}/${formatNumber(cell.completed)}/${formatNumber(cell.assigned)}`,
                title: `Đúng ${formatNumber(cell.correct)} · Đã làm ${formatNumber(cell.completed)} · Được giao ${formatNumber(cell.assigned)}`,
                tone: completionTone(percent),
            };
        }
        case 'absent':
            return { text: 'Vắng', title: `Vắng – tính 0/0/${formatNumber(cell.assigned)}`, tone: 'absent' };
        case 'pending':
            return { text: 'Chưa chấm', title: 'Có đi học nhưng chưa nhập BTVN', tone: 'muted' };
        case 'unmarked':
            return { text: 'Chưa ĐD', title: 'Chưa được điểm danh', tone: 'muted' };
        case 'no_session':
            return { text: '—', title: 'Chưa tạo buổi học', tone: 'muted' };
        default:
            return { text: '', title: 'Chưa vào lớp', tone: 'plain' };
    }
}

export function scoreTone(score: number): CellTone {
    if (score >= 8) return 'good';
    if (score >= 5) return 'fair';
    return 'poor';
}

export function scoreCellView(cell: GradebookScoreCell): CellView {
    switch (cell.kind) {
        case 'score':
            return { text: formatNumber(cell.score), tone: scoreTone(cell.score) };
        case 'absent':
            return { text: 'Vắng', title: 'Vắng – không tính vào điểm trung bình', tone: 'absent' };
        case 'missing':
            return { text: '—', title: 'Chưa có điểm', tone: 'muted' };
        default:
            return { text: '', title: 'Chưa vào lớp', tone: 'plain' };
    }
}

export const CELL_TONE_CLASSES: Record<CellTone, string> = {
    good: 'bg-success-50 text-success-800',
    fair: 'bg-warning-50 text-warning-800',
    poor: 'bg-danger-50 text-danger-800',
    absent: 'text-danger-700',
    muted: 'text-ink-400',
    plain: 'text-ink-900',
};

// Sổ điểm của một học sinh: gộp các lớp thành một danh sách theo ngày.
// Bỏ các buổi trước khi em vào lớp; ngày có lịch mà chưa tạo buổi vẫn giữ (hiện "—"),
// trừ lớp em đã nghỉ: lớp đó chỉ giữ các buổi đã có.
export interface StudentHomeworkEntry {
    key: string;
    dateKey: string;
    classId: string;
    className: string;
    cell: GradebookHomeworkCell;
}

export interface StudentTestEntry {
    key: string;
    dateKey: string;
    classId: string;
    className: string;
    name: string;
    weight: number;
    cell: GradebookScoreCell;
}

type StudentGradebookLike = {
    classes: ReadonlyArray<{
        id: string;
        name: string;
        isActive?: boolean;
        dates: ReadonlyArray<{ dateKey: string }>;
        assessments: ReadonlyArray<{ id: string; name: string; weight: number; dateKey: string }>;
        row: { homework: GradebookHomeworkCell[]; scores: GradebookScoreCell[] };
    }>;
};

const byDateThenClass = (a: { dateKey: string; className: string }, b: { dateKey: string; className: string }) =>
    a.dateKey.localeCompare(b.dateKey) || a.className.localeCompare(b.className, 'vi');

export function studentHomeworkEntries(data: StudentGradebookLike, classId = 'all'): StudentHomeworkEntry[] {
    return data.classes
        .filter(item => classId === 'all' || item.id === classId)
        .flatMap(item => item.dates.map((date, index) => ({
            key: `${item.id}:${date.dateKey}`,
            dateKey: date.dateKey,
            classId: item.id,
            className: item.name,
            cell: item.row.homework[index],
            left: item.isActive === false,
        })))
        .filter(entry => entry.cell && entry.cell.kind !== 'not_enrolled' && !(entry.left && entry.cell.kind === 'no_session'))
        .map(({ left: _left, ...entry }) => entry)
        .sort(byDateThenClass);
}

export function studentTestEntries(data: StudentGradebookLike, classId = 'all'): StudentTestEntry[] {
    return data.classes
        .filter(item => classId === 'all' || item.id === classId)
        .flatMap(item => item.assessments.map((assessment, index) => ({
            key: assessment.id,
            dateKey: assessment.dateKey,
            classId: item.id,
            className: item.name,
            name: assessment.name,
            weight: assessment.weight,
            cell: item.row.scores[index],
        })))
        .filter(entry => entry.cell && entry.cell.kind !== 'not_enrolled')
        .sort(byDateThenClass);
}
